import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, formatApiError } from "@/lib/api";
import { useT, LanguageSwitcher } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { ArrowLeft } from "@phosphor-icons/react";

export default function RingLogin() {
  const nav = useNavigate();
  const t = useT();
  const [params] = useSearchParams();
  const quickToken = params.get("token");
  const [form, setForm] = useState({
    event_id: params.get("event_id") || "",
    ring_id: params.get("ring_id") || "",
    ring_password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const store = (data) => {
    localStorage.setItem("ring_session", JSON.stringify({ ...data, ring_password: data.ring.password || form.ring_password }));
    toast.success(t("rl.connected", { name: data.ring.name }));
    nav(`/ring/${data.ring.id}/control`);
  };

  useEffect(() => {
    if (!quickToken) return;
    (async () => {
      try {
        const { data } = await axios.post(`${API}/auth/ring-quick-login`, { token: quickToken });
        store(data);
      } catch (err) {
        setError(formatApiError(err.response?.data?.detail) || err.message);
      }
    })();
    // eslint-disable-next-line
  }, [quickToken]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/ring-login`, form);
      store(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (quickToken && !error) {
    return <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">{t("rl.connecting")}</div>;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <button onClick={() => nav("/login")} className="absolute top-4 left-4 z-20 flex items-center gap-2 text-sm text-muted-foreground hover:text-white transition-colors" data-testid="ring-login-back">
        <ArrowLeft size={16} /> {t("rl.back")}
      </button>
      <div className="absolute top-4 right-4 z-20"><LanguageSwitcher /></div>
      <form onSubmit={submit} className="w-full max-w-sm space-y-6 bg-card border border-border rounded-lg p-8" data-testid="ring-login-form">
        <div className="flex items-center gap-2 text-brand-yellow font-heading font-extrabold text-xl">
          <Logo size={24} className="text-brand-yellow" /> {t("rl.title")}
        </div>
        <p className="text-sm text-muted-foreground">{t("rl.sub")}</p>
        <div className="space-y-2">
          <Label>Event ID</Label>
          <Input data-testid="ring-event-id" value={form.event_id} onChange={set("event_id")} required />
        </div>
        <div className="space-y-2">
          <Label>Ring ID</Label>
          <Input data-testid="ring-id-input" value={form.ring_id} onChange={set("ring_id")} required />
        </div>
        <div className="space-y-2">
          <Label>{t("ring.password")}</Label>
          <Input data-testid="ring-password" value={form.ring_password} onChange={set("ring_password")} required />
        </div>
        {error && <p className="text-sm text-destructive" data-testid="ring-login-error">{error}</p>}
        <Button type="submit" className="w-full h-11 font-semibold" data-testid="ring-login-submit" disabled={loading}>
          {loading ? t("rl.connecting") : t("rl.connect")}
        </Button>
      </form>
    </div>
  );
}
